import { Field, Form, Formik, FormikProps, getIn, useFormikContext } from 'formik';
import { FunctionComponent, useCallback, useRef, useState } from 'react';
import * as Yup from 'yup';
import FieldError from '../../forms/FieldError';
import Label from '../../forms/Label';
import MaxTextField from '../../forms/MaxTextField';
import RadioButton from '../../forms/RadioButton';
import RadioButtonGroup from '../../forms/RadioButtonGroup';
import TextArea from '../../forms/TextArea';
import { requiredValidator } from '../../forms/validators';
import OverlayAlertModal from '../../modal/OverlayAlertModal';
import OverlayConfirmModal from '../../modal/OverlayConfirmModal';
import { PropertyAssociationInfo } from '../../propertyAssociation/models/PropertyAssociationInfo';
import { usePropertyAssociationContext } from '../../propertyAssociation/PropertyAssociationContext';
import { PinboardMessageFormModel } from '../models/PinboardMessageFormModel';
import PinboardMessageFileInput from './PinboardMessageFileInput';

type Props = {
    isNew?: boolean;
    messageForm: PinboardMessageFormModel;
    submitBtnText?: string;
    user?: { imagePath: string; firstName: string; lastName: string };
    onValidSubmit: (values: PinboardMessageFormModel) => void;
    onCancel: () => void;
};

const validationSchema = Yup.object().shape({
    title: requiredValidator.max(30, 'Ämnet får vara max 30 tecken.'),
    text: requiredValidator.max(10000, 'Meddelandet får vara max 10 000 tecken.'),
    canUserAnswer: Yup.string()
        .nullable()
        .matches(/ja|nej/, 'Ogiltigt värde')
        .required('Välj om meddelandet skall gå att svara på'),
    target: Yup.string().nullable().required('Välj mottagare'),
    targetAssociations: Yup.array().when('target', {
        is: 'some',
        then: (schema) => schema.required('Välj minst ett engagemang').min(1, 'Välj minst ett engagemang'),
    }),
});

const PinboardMessageForm: FunctionComponent<Props> = ({ isNew = false, messageForm, onCancel, onValidSubmit, submitBtnText }) => {
    const formikRef = useRef<FormikProps<any>>(null);
    const { currentArea, currentPropertyAssociation, allPropertyAssociations, isRBHyr } = usePropertyAssociationContext();
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const canPublishToSeveral = isRBHyr && allPropertyAssociations.length > 1;

    const handleSubmit = useCallback(
        (values: PinboardMessageFormModel) => {
            const target = values.target;
            if (target === 'all' || target === 'some') {
                setShowConfirmModal(true);
            } else {
                onValidSubmit(values);
            }
        },
        [onValidSubmit],
    );

    const onTrySubmit = useCallback(() => {
        if (!validationSchema.isValidSync(formikRef.current?.values)) {
            setShowErrorModal(true);
        }
    }, []);

    const onAddFiles = useCallback((newFiles: File | File[]) => {
        if (!formikRef.current) {
            return;
        }

        const { files, filePaths } = formikRef.current.values;

        if (!Array.isArray(newFiles)) {
            newFiles = [newFiles];
        }

        // Remove all files that are already in the files or oldFiles array
        newFiles = newFiles.filter((file: File) => !files.some((f: File) => f.name === file.name) && !(filePaths && filePaths.some((f: string) => f.endsWith('/' + file.name))));

        formikRef.current.setFieldValue('files', [...files, ...newFiles]);
    }, []);

    const onDeleteOldFile = useCallback((fileName: string) => {
        if (!formikRef.current) {
            return;
        }

        const { filePaths } = formikRef.current.values;
        formikRef.current.setFieldValue(
            'filePaths',
            filePaths.filter((path: string) => path !== fileName),
        );
    }, []);

    const onDeleteNewFile = useCallback((index: number) => {
        if (!formikRef.current) {
            return;
        }

        const { files } = formikRef.current.values;
        formikRef.current.setFieldValue(
            'files',
            files.filter((file: File, i: number) => i !== index),
        );
    }, []);

    return (
        <>
            <Formik initialValues={messageForm} innerRef={formikRef as any} enableReinitialize={true} validationSchema={validationSchema} validateOnMount={true} onSubmit={handleSubmit}>
                {({ values, errors, touched }) => {
                    const formProps = { values, errors, touched };

                    return (
                        <Form>
                            <div className="col-12 col-md-8 px-4 ms-2">
                                <div className="mb-5 position-relative">
                                    <MaxTextField maxLength={30} label="Rubrik" name="title" {...formProps} />
                                </div>
                                <TextArea label="Meddelande" name="text" {...formProps}></TextArea>
                                {isNew && currentArea?.propertyAssociationAreaId && (
                                    <div className="my-4">
                                        <p className="my-0">Välj mottagare</p>
                                        <RadioButtonGroup {...formProps} name="target">
                                            {canPublishToSeveral && <PublishToSeveralOption propertyAssociations={allPropertyAssociations} />}
                                            <div className="my-3">
                                                <RadioButton
                                                    name="target"
                                                    label={`Alla boende${canPublishToSeveral ? ' i ' + currentPropertyAssociation!.name : ''} (i alla områden)`}
                                                    value="current"
                                                />
                                            </div>
                                            <div className="mt-3">
                                                <RadioButton name="target" label={`Endast boende i ${currentArea.name}`} value={currentArea.propertyAssociationAreaId} />
                                            </div>
                                        </RadioButtonGroup>
                                    </div>
                                )}
                                <div className="mb-2">
                                    <p className="my-0">Ska meddelandet gå att svara på?</p>
                                    <p className="meta mb-4 mt-2">Ska de boende kunna svara på meddelandet via appen.</p>
                                    <RadioButtonGroup {...formProps} name="canUserAnswer">
                                        <div className="my-3">
                                            <RadioButton name="canUserAnswer" label="Nej" value="nej" />
                                        </div>
                                        <div className="mt-3">
                                            <RadioButton name="canUserAnswer" label="Ja" value="ja" />
                                        </div>
                                    </RadioButtonGroup>
                                </div>
                                <PinboardMessageFileInput
                                    oldFiles={values.filePaths}
                                    newFiles={values.files}
                                    onAddFiles={onAddFiles}
                                    onDeleteOldFile={onDeleteOldFile}
                                    onDeleteNewFile={onDeleteNewFile}
                                />
                            </div>
                            <div className="paper__buttons">
                                <div className="paper__secondary-buttons">
                                    <button type="button" className="btn btn-lg btn-secondary" onClick={onCancel}>
                                        Avbryt
                                    </button>
                                </div>
                                <div className="paper__primary-buttons">
                                    <button type="submit" className="btn btn-lg btn-success" onClick={onTrySubmit}>
                                        {submitBtnText || 'Spara'}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
            <OverlayAlertModal
                heading="Det saknas information"
                text={`Det verkar som att viss obligatorisk information för meddelandet saknas. När du fyllt i allt kommer du kunna ${submitBtnText?.toLocaleLowerCase() || 'spara'} det.`}
                visible={showErrorModal}
                onClick={() => setShowErrorModal(false)}
            />
            <OverlayConfirmModal
                heading="Publicera inlägg till engagemang"
                text="Är du säker på att du vill publicera inlägget till alla boende i följande engagemang?"
                visible={showConfirmModal}
                okButtonText="Publicera"
                cancelButtonText="Avbryt"
                onCancel={() => setShowConfirmModal(false)}
                onSubmit={() => {
                    setShowConfirmModal(false);
                    if (formikRef.current) {
                        onValidSubmit(formikRef.current.values);
                    }
                }}
            >
                {allPropertyAssociations.length > 0 && formikRef.current && (
                    <ul className="mb-5 mx-auto text-start" style={{ width: 'fit-content' }}>
                        {allPropertyAssociations
                            .filter(
                                (pa) =>
                                    formikRef.current!.values.target === 'all' ||
                                    (formikRef.current!.values.target === 'some' && formikRef.current!.values.targetAssociations?.includes(pa.propertyAssociationId)) ||
                                    (formikRef.current!.values.target === 'current' && pa.propertyAssociationId === currentPropertyAssociation?.propertyAssociationId),
                            )
                            .map((association) => (
                                <li key={association.propertyAssociationId}>
                                    <span>{association.name}</span>
                                </li>
                            ))}
                    </ul>
                )}
            </OverlayConfirmModal>
        </>
    );
};

export default PinboardMessageForm;

const PublishToSeveralOption = ({ propertyAssociations }: { propertyAssociations: PropertyAssociationInfo[] }) => {
    const { values, errors, touched, submitCount } = useFormikContext<PinboardMessageFormModel>();
    const error = getIn(errors, 'targetAssociations');
    const isTouched = getIn(touched, 'targetAssociations');
    const showError = error && (isTouched || submitCount > 0);

    if (propertyAssociations.length === 3) {
        return (
            <div className="my-3">
                <RadioButton name="target" label="Alla boende (i alla mina engagemang)" value="all" />
            </div>
        );
    }

    return (
        <div className="my-3">
            <RadioButton name="target" label="Alla boende (i flera engagemang)" value="some" />
            {values.target === 'some' && (
                <div className="d-grid ms-4 ps-2">
                    <Label name="targetAssociations" text="Välj ett eller flera engagemang" />
                    <small id="targetAssociations-hint" style={{ letterSpacing: '-0.56px' }}>
                        Håll in <kbd style={{ all: 'revert' }}>CTRL</kbd> (Windows) eller <kbd style={{ all: 'revert' }}>CMD</kbd> (Mac) för att välja flera
                    </small>
                    <Field
                        as="select"
                        multiple
                        name="targetAssociations"
                        id="targetAssociations"
                        className={showError ? 'is-invalid' : ''}
                        aria-details="targetAssociations-hint"
                        style={{ resize: 'vertical' }}
                    >
                        {propertyAssociations.map((association) => (
                            <option key={association.propertyAssociationId} value={association.propertyAssociationId}>
                                {association.name}
                            </option>
                        ))}
                    </Field>
                    <FieldError name="targetAssociations" errors={errors} touched={touched} ignoreTouched={showError} />
                </div>
            )}
        </div>
    );
};
